/**
 * Field utilities - helper functions for field management and fallback generation
 */
import { prettifyLabel } from "@/lib/text-utils";
/**
 * Converts a field key to a human-readable label
 */
export const guessFieldLabel = (key) => {
    return prettifyLabel(extractFieldName(key));
};
/**
 * Guesses field type based on key patterns and common naming conventions
 */
export const guessFieldType = (key) => {
    const lowerKey = key.split(".").pop().toLowerCase();
    // Date patterns
    if (lowerKey.includes("date") ||
        lowerKey.includes("_at") ||
        lowerKey.endsWith("_time") ||
        lowerKey.includes("created") ||
        lowerKey.includes("updated")) {
        return "date";
    }
    // Percentage patterns
    if (lowerKey.includes("percent") ||
        lowerKey.includes("porcent") ||
        lowerKey.includes("por_") ||
        lowerKey.startsWith("fde_") ||
        lowerKey.includes("crec") ||
        lowerKey.includes("conseguido") ||
        lowerKey.includes("kpi")) {
        return "percent";
    }
    // Currency patterns
    if (lowerKey.includes("venta") ||
        lowerKey.includes("precio") ||
        lowerKey.includes("cost") ||
        lowerKey.includes("amount") ||
        lowerKey.includes("euros") ||
        lowerKey.includes("currency")) {
        return "currency";
    }
    // Integer patterns (IDs, counts, etc.)
    if (lowerKey === "id" ||
        lowerKey.endsWith("_id") ||
        lowerKey.includes("cliente") ||
        lowerKey.includes("count") ||
        lowerKey.includes("num") ||
        lowerKey.includes("ano") ||
        lowerKey.includes("mes") ||
        lowerKey.includes("year") ||
        lowerKey.includes("month")) {
        return "integer";
    }
    // Float patterns (volumes, measurements, etc.)
    if (lowerKey.includes("vol") ||
        lowerKey.includes("obj") ||
        lowerKey.includes("rtdo") ||
        lowerKey.includes("resultado") ||
        lowerKey.includes("objetivo") ||
        lowerKey.includes("cajas") ||
        lowerKey.includes("prom") ||
        lowerKey.includes("acum") ||
        lowerKey.includes("ptos") ||
        lowerKey.includes("puntos")) {
        return "float";
    }
    // Default to string for everything else
    return "string";
};
/**
 * Creates a fallback field configuration for unknown keys
 */
export const createFallbackField = (key) => {
    const type = guessFieldType(key);
    const label = `${guessFieldLabel(key)}`;
    return {
        key,
        label,
        type,
        align: type === "string" ? "left" : "right",
    };
};
/**
 * Extracts table name from a composite key
 */
export const extractTableName = (key) => {
    const dotIndex = key.indexOf(".");
    return dotIndex !== -1 ? key.substring(0, dotIndex) : "";
};
/**
 * Extracts field name from a composite key
 */
export const extractFieldName = (key) => {
    const dotIndex = key.indexOf(".");
    return dotIndex !== -1 ? key.substring(dotIndex + 1) : key;
};
/**
 * Creates a composite key from table name and field name
 */
export const createCompositeKey = (tableName, fieldName) => {
    return `${tableName}.${fieldName}`;
};
